import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { Observable, Subject, combineLatest, firstValueFrom } from 'rxjs';
import {
  map, startWith, takeUntil,
} from 'rxjs/operators';
import { IStoreFavoriteFolderDetails } from 'src/api/activity/favorite-folder/favorite-folder.store';
import {
  AddNewPinbordFolderDialogComponent,
} from 'src/components/dialogs/add-new-pinbord-folder-dialog/add-new-pinbord-folder-dialog.component';
import { EOrganizationUnitType } from 'src/components/dialogs/add-organization-dialog/add-organization-dialog.component';
import { I18nToastProvider } from 'src/providers/i18n-toast.provider';
import { PINBORD_FOLDER_LIMIT, PinbordProvider } from 'src/providers/pinbord.provider';
import { DoenkidsSessionProvider } from 'src/providers/session.provider';
import { IFavoriteFolderDetails } from 'typings/doenkids/doenkids';

@Component({
    selector: 'app-pinbord',
    templateUrl: './pinbord.component.html',
    styleUrls: ['./pinbord.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class PinbordComponent implements OnInit, OnDestroy {
  private stop$: Subject<void> = new Subject<void>();

  public currentFolders$: Observable<IStoreFavoriteFolderDetails>;

  public currentPage = 1;

  public viewSharedFoldersControl = new FormControl(true);

  public itemsPerPage = 11;

  /**
   * We display the name in the template
   */
  public currentOuName$: Observable<string>;

  private currentOuId$: Observable<number>;

  public isReader$: Observable<boolean>;

  constructor(
    private dialog: MatDialog,
    private router: Router,
    $pinbord: PinbordProvider,
    private $session: DoenkidsSessionProvider,
    private $i18nToastProvider: I18nToastProvider,
  ) {
    this.currentFolders$ = combineLatest([$pinbord.currentFolders$.asObservable(), this.viewSharedFoldersControl.valueChanges.pipe(
      startWith(this.viewSharedFoldersControl.value),
    ), this.$session.currentOuId$]).pipe(
      map(([folders, viewShared, currentOuId]) => {
        if (viewShared) {
          return folders;
        }
        return {
          ...folders,
          items: folders.folders.filter((folder) => {
            // If view shared is true, we show all folders
            // Otherwise we filter the folders by the current organization unit

            if (viewShared) return true;

            return folder.organization_unit_id === currentOuId;
          }),
        };
      }),
    );

    this.isReader$ = this.$session.isReader$.pipe(takeUntil(this.stop$));

    this.currentOuName$ = this.$session.getOrganizationUnit$.pipe(
      takeUntil(this.stop$),
      map((ou) => ou.name),
    );

    this.currentOuId$ = this.$session.currentOuId$.pipe(
      takeUntil(this.stop$),
    );
  }

  ngOnInit() {
    // When a new organization is rewrite the current url
    //
    this.$session.getOrganizationUnitSwitch$.pipe(takeUntil(this.stop$)).subscribe((organizationUnit) => {
      if (parseInt(`${organizationUnit.organization_unit_type_id}`, 10) !== EOrganizationUnitType.LOCATION) {
        this.router.navigate(['/'], { replaceUrl: true });
      } else {
        this.router.navigate([`/organization/${organizationUnit.id}/pinbord`], { replaceUrl: true });
      }
    });
  }

  ngOnDestroy() {
    this.stop$.next();
  }

  async goToFolder(folder: IFavoriteFolderDetails) {
    const ouId = await firstValueFrom(this.currentOuId$);
    this.router.navigate([`/organization/${ouId}/pinbord/${folder.id}`]);
  }

  async addNewFolder() {
    const currentFolders = await firstValueFrom(this.currentFolders$);
    if (currentFolders?.totalNoOfFolders >= PINBORD_FOLDER_LIMIT) {
      this.$i18nToastProvider.error(_('pinboard.limit.reached'), { limit: PINBORD_FOLDER_LIMIT });
      return;
    }
    this.dialog.open(AddNewPinbordFolderDialogComponent, {});
  }

  paginate(page: number) {
    this.currentPage = page;
  }
}
